import React from 'react';

const DataPreview = ({ 
  previewData, 
  mappings, 
  pcgsUpdates, 
  revertedUpdates, 
  onRevertPcgsUpdates,
  updateFromPCGS,
  availableFields 
}) => {
  // Get all unique fields from mapped fields and PCGS updates
  const getAllFields = () => {
    const allFields = new Set(Object.values(mappings));
    
    // Add all possible PCGS fields
    const pcgsFields = [
      'certification_number', 'grade', 'variety', 'designation', 'population',
      'pop_higher', 'price_guide_value', 'cac_sticker', 'mintage', 'composition',
      'weight', 'diameter', 'year', 'country', 'denomination', 'mint_mark',
      'special_attributes', 'certification_date', 'last_updated'
    ];
    
    pcgsFields.forEach(field => allFields.add(field));

    // Add fields from PCGS updates
    const pcgsUpdateFields = new Set();
    Object.values(pcgsUpdates).forEach(update => {
      Object.keys(update.updates).forEach(field => pcgsUpdateFields.add(field));
    });
    
    // Add formatted PCGS fields
    pcgsUpdateFields.forEach(field => {
      if (!allFields.has(field)) {
        // Only format fields that weren't in the original mapping
        allFields.add(field);
      }
    });

    return Array.from(allFields);
  };

  const formatFieldName = (fieldKey) => {
    return fieldKey
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatValue = (value, fieldKey) => {
    if (value === null || value === undefined) return '-';

    // Handle price fields (show with 2 decimal places)
    if (['price_guide_value', 'purchase_price', 'list_price', 'shipping_cost', 'grading_cost', 'total_cost'].includes(fieldKey)) {
      const num = parseFloat(value);
      return !isNaN(num) ? `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : value.toString();
    }

    // Handle numeric fields that should have commas
    if (['mintage', 'population', 'pop_higher', 'current_population', 'current_pop_higher'].includes(fieldKey)) {
      const num = parseInt(value);
      return !isNaN(num) ? num.toLocaleString() : value.toString();
    }

    // Handle weight and diameter (show with up to 4 decimal places)
    if (['weight', 'diameter'].includes(fieldKey)) {
      const num = parseFloat(value);
      return !isNaN(num) ? num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 }) : value.toString();
    }

    return value.toString();
  };

  const renderCell = (rowIndex, fieldKey, row) => {
    const field = availableFields.find(f => f.key === fieldKey) || { key: fieldKey, label: fieldKey };
    let value;

    // Handle images specially
    if (fieldKey === 'images') {
      const images = row[fieldKey];
      if (Array.isArray(images) && images.length > 0) {
        return (
          <div className="flex gap-2 overflow-x-auto">
            {images.map((img, idx) => (
              <a 
                key={idx} 
                href={img.image_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block"
              >
                <img 
                  src={img.image_url} 
                  alt={`Coin ${idx + 1}`} 
                  className="h-10 w-10 object-cover rounded flex-shrink-0"
                />
              </a>
            ))}
          </div>
        );
      }
      return '-';
    }

    // Get the value based on whether this row's updates have been reverted
    if (revertedUpdates.has(rowIndex)) {
      // Use original value if updates were reverted
      value = formatValue(pcgsUpdates[rowIndex]?.original?.[fieldKey] ?? row[fieldKey], fieldKey);
    } else {
      // Check for PCGS updates
      const pcgsUpdate = pcgsUpdates[rowIndex]?.updates?.[fieldKey];
      const originalValue = pcgsUpdates[rowIndex]?.original?.[fieldKey];
      
      if (pcgsUpdate && String(pcgsUpdate).trim() !== String(originalValue).trim()) {
        return (
          <div>
            {formatValue(pcgsUpdate, fieldKey)}
            <span className="ml-2 text-xs text-gray-500">
              (Original: {formatValue(originalValue, fieldKey)})
            </span>
          </div>
        );
      }
      
      // Handle population fields
      if (fieldKey === 'population_when_bought' || fieldKey === 'current_population') {
        value = formatValue(row['population'], 'population');
      } else if (fieldKey === 'pop_higher_when_bought' || fieldKey === 'current_pop_higher') {
        value = formatValue(row['pop_higher'], 'pop_higher');
      } else {
        value = formatValue(pcgsUpdate ?? row[fieldKey], fieldKey);
      }
    }

    return value;
  };

  return (
    <div className="bg-white shadow sm:rounded-lg mt-6">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Data Preview</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Row
                </th>
                {getAllFields().map(fieldKey => {
                  // For fields from PCGS that weren't in the original mapping, format their names
                  const field = availableFields.find(f => f.key === fieldKey) || 
                    (pcgsUpdates[0]?.updates?.[fieldKey] ? 
                      { key: fieldKey, label: formatFieldName(fieldKey) } : 
                      { key: fieldKey, label: fieldKey }
                    );
                  return (
                    <th 
                      key={field.key}
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {field.label || formatFieldName(fieldKey)}
                      {pcgsUpdates[0]?.updates?.[fieldKey] && 
                        <span className="ml-1 text-green-600">*</span>
                      }
                    </th>
                  );
                })}
                {updateFromPCGS && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {previewData.map((row, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {index + 1}
                  </td>
                  {getAllFields().map(fieldKey => (
                    <td 
                      key={fieldKey}
                      className={`px-6 py-4 ${fieldKey === 'images' ? '' : 'whitespace-nowrap'} text-sm ${
                        pcgsUpdates[index]?.updates?.[fieldKey] && 
                        String(pcgsUpdates[index].updates[fieldKey]).trim() !== 
                        String(pcgsUpdates[index].original[fieldKey]).trim() &&
                        !revertedUpdates.has(index)
                          ? 'text-green-600 font-medium' 
                          : 'text-gray-900'
                      }`}
                    >
                      {renderCell(index, fieldKey, row)}
                    </td>
                  ))}
                  {updateFromPCGS && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {pcgsUpdates[index] && !revertedUpdates.has(index) && (
                        <button
                          onClick={() => onRevertPcgsUpdates(index)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Revert PCGS Updates
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DataPreview;
